/**
 * AxLabs Experiments - Mathematical Primitives
 *
 * Shared mathematical functions commonly used across physics, chemistry,
 * and biology experiments. These are pure functions with no side effects,
 * making them ideal for use within derivative computations.
 */
// ============================================================================
// PHYSICAL CONSTANTS
// ============================================================================
/**
 * Gravitational constant G = 6.674×10⁻¹¹ N·m²/kg²
 */
export const GRAVITATIONAL_CONSTANT = 6.67430e-11;
/**
 * Boltzmann constant k_B = 1.380649×10⁻²³ J/K
 */
export const BOLTZMANN_CONSTANT = 1.380649e-23;
/**
 * Avogadro's number N_A = 6.02214076×10²³ mol⁻¹
 */
export const AVOGADRO_NUMBER = 6.02214076e23;
/**
 * Ideal gas constant R = 8.314462618 J/(mol·K)
 */
export const IDEAL_GAS_CONSTANT = 8.314462618;
/**
 * Planck constant h = 6.62607015×10⁻³⁴ J·s
 */
export const PLANCK_CONSTANT = 6.62607015e-34;
/**
 * Speed of light c = 299,792,458 m/s
 */
export const SPEED_OF_LIGHT = 299_792_458;
/**
 * Elementary charge e = 1.602176634×10⁻¹⁹ C
 */
export const ELEMENTARY_CHARGE = 1.602176634e-19;
/**
 * Vacuum permittivity ε₀ = 8.8541878128×10⁻¹² F/m
 */
export const VACUUM_PERMITTIVITY = 8.8541878128e-12;
/**
 * Coulomb constant k_e = 1/(4πε₀) ≈ 8.987×10⁹ N·m²/C²
 */
export const COULOMB_CONSTANT = 1 / (4 * Math.PI * VACUUM_PERMITTIVITY);
/**
 * Standard atmospheric pressure = 101,325 Pa
 */
export const STANDARD_PRESSURE = 101_325;
/**
 * Standard temperature = 273.15 K (0°C)
 */
export const STANDARD_TEMPERATURE = 273.15;
/**
 * Acceleration due to gravity at Earth's surface g = 9.80665 m/s²
 */
export const STANDARD_GRAVITY = 9.80665;
// ============================================================================
// OSCILLATORY MOTION
// ============================================================================
/**
 * Calculate the natural frequency of a damped harmonic oscillator.
 *
 * The damped frequency is given by:
 * ω = sqrt(ω₀² - γ²)
 *
 * where ω₀ is the natural frequency and γ is the damping coefficient.
 * Returns 0 if the system is overdamped (γ ≥ ω₀).
 *
 * @param omega0 - Natural (undamped) angular frequency in rad/s
 * @param gamma - Damping coefficient in 1/s
 * @returns Damped angular frequency in rad/s, or 0 if overdamped
 *
 * @example
 * ```ts
 * const k = 100; // spring constant N/m
 * const m = 1;   // mass kg
 * const b = 0.5; // damping kg/s
 * const omega0 = Math.sqrt(k / m);
 * const gamma = b / (2 * m);
 * const omega = dampedOscillator(omega0, gamma);
 * ```
 */
export function dampedOscillator(omega0, gamma) {
    const discriminant = omega0 * omega0 - gamma * gamma;
    return discriminant > 0 ? Math.sqrt(discriminant) : 0;
}
/**
 * Calculate the displacement of a damped oscillator at time t.
 *
 * @param amplitude - Initial amplitude
 * @param omega0 - Natural angular frequency (rad/s)
 * @param gamma - Damping coefficient (1/s)
 * @param phase - Initial phase (rad)
 * @param time - Current time (s)
 * @returns Displacement at given time
 */
export function dampedOscillatorDisplacement(amplitude, omega0, gamma, phase, time) {
    const omega = dampedOscillator(omega0, gamma);
    return amplitude * Math.exp(-gamma * time) * Math.cos(omega * time + phase);
}
/**
 * Calculate the quality factor Q of a damped oscillator.
 *
 * Q = ω₀ / (2γ)
 *
 * @param omega0 - Natural angular frequency (rad/s)
 * @param gamma - Damping coefficient (1/s)
 * @returns Quality factor (higher = less damping)
 */
export function qualityFactor(omega0, gamma) {
    return gamma !== 0 ? omega0 / (2 * gamma) : Infinity;
}
// ============================================================================
// EXPONENTIAL PROCESSES
// ============================================================================
/**
 * Calculate exponential decay.
 *
 * N(t) = N₀ * e^(-kt)
 *
 * Commonly used for:
 * - Radioactive decay (k = decay constant)
 * - RC circuit discharge
 * - Damping in oscillatory systems
 *
 * @param initial - Initial value N₀
 * @param rate - Decay constant k in 1/s
 * @param time - Time t in seconds
 * @returns Value after decay
 *
 * @example
 * ```ts
 * // Half-life decay
 * const halfLife = 5; // seconds
 * const k = Math.LN2 / halfLife;
 * const remaining = exponentialDecay(100, k, 10); // ~25
 * ```
 */
export function exponentialDecay(initial, rate, time) {
    return initial * Math.exp(-rate * time);
}
/**
 * Calculate the half-life from a decay constant.
 *
 * t₁/₂ = ln(2) / k
 *
 * @param rate - Decay constant k in 1/s
 * @returns Half-life in seconds
 */
export function halfLifeFromDecayConstant(rate) {
    return Math.LN2 / rate;
}
/**
 * Calculate the decay constant from a half-life.
 *
 * k = ln(2) / t₁/₂
 *
 * @param halfLife - Half-life in seconds
 * @returns Decay constant in 1/s
 */
export function decayConstantFromHalfLife(halfLife) {
    return Math.LN2 / halfLife;
}
/**
 * Calculate exponential growth.
 *
 * N(t) = N₀ * e^(kt)
 *
 * @param initial - Initial value N₀
 * @param rate - Growth rate k in 1/s
 * @param time - Time t in seconds
 * @returns Value after growth
 */
export function exponentialGrowth(initial, rate, time) {
    return initial * Math.exp(rate * time);
}
// ============================================================================
// POPULATION DYNAMICS
// ============================================================================
/**
 * Calculate logistic growth.
 *
 * dN/dt = r * N * (1 - N/K)
 *
 * The analytical solution is:
 * N(t) = K / (1 + ((K - N₀) / N₀) * e^(-rt))
 *
 * @param initial - Initial population N₀
 * @param rate - Intrinsic growth rate r in 1/s
 * @param carryingCapacity - Carrying capacity K
 * @param time - Time t in seconds
 * @returns Population at given time
 *
 * @example
 * ```ts
 * // Bacteria growth
 * const n0 = 10;
 * const r = 0.1;  // per hour
 * const K = 1000;
 * const population = logisticGrowth(n0, r, K, 24); // after 24 hours
 * ```
 */
export function logisticGrowth(initial, rate, carryingCapacity, time) {
    if (carryingCapacity <= 0)
        return 0;
    const exponent = -rate * time;
    const denominator = 1 + ((carryingCapacity - initial) / initial) * Math.exp(exponent);
    return carryingCapacity / denominator;
}
/**
 * Calculate the rate of change for logistic growth.
 *
 * Use this for differential equation systems.
 *
 * @param population - Current population N
 * @param rate - Intrinsic growth rate r
 * @param carryingCapacity - Carrying capacity K
 * @returns dN/dt
 */
export function logisticGrowthDerivative(population, rate, carryingCapacity) {
    return rate * population * (1 - population / carryingCapacity);
}
/**
 * Calculate competitive Lotka-Volterra dynamics (two species).
 *
 * dN₁/dt = r₁ * N₁ * (1 - (N₁ + α₁₂ * N₂) / K₁)
 * dN₂/dt = r₂ * N₂ * (1 - (N₂ + α₂₁ * N₁) / K₂)
 *
 * @param n1 - Population of species 1
 * @param n2 - Population of species 2
 * @param r1 - Growth rate of species 1
 * @param r2 - Growth rate of species 2
 * @param k1 - Carrying capacity for species 1
 * @param k2 - Carrying capacity for species 2
 * @param alpha12 - Competition coefficient (effect of species 2 on species 1)
 * @param alpha21 - Competition coefficient (effect of species 1 on species 2)
 * @returns [dN₁/dt, dN₂/dt]
 */
export function lotkaVolterraCompetition(n1, n2, r1, r2, k1, k2, alpha12, alpha21) {
    const dn1dt = r1 * n1 * (1 - (n1 + alpha12 * n2) / k1);
    const dn2dt = r2 * n2 * (1 - (n2 + alpha21 * n1) / k2);
    return [dn1dt, dn2dt];
}
// ============================================================================
// CHEMICAL KINETICS
// ============================================================================
/**
 * Calculate reaction rate constant using the Arrhenius equation.
 *
 * k = A * e^(-Ea / (R * T))
 *
 * @param preExponential - Pre-exponential factor A (same units as k)
 * @param activationEnergy - Activation energy Ea in J/mol
 * @param temperature - Temperature T in Kelvin
 * @param gasConstant - Gas constant R (default: 8.314 J/(mol·K))
 * @returns Rate constant k
 *
 * @example
 * ```ts
 * const A = 1e10; // 1/s
 * const Ea = 50000; // J/mol (50 kJ/mol)
 * const T = 298; // K (25°C)
 * const k = arrheniusRate(A, Ea, T);
 * ```
 */
export function arrheniusRate(preExponential, activationEnergy, temperature, gasConstant = IDEAL_GAS_CONSTANT) {
    if (temperature <= 0)
        return 0;
    return preExponential * Math.exp(-activationEnergy / (gasConstant * temperature));
}
/**
 * Calculate reaction rate for a general reaction order.
 *
 * rate = k * [A]^n
 *
 * @param rateConstant - Rate constant k
 * @param concentration - Reactant concentration [A]
 * @param order - Reaction order n
 * @returns Reaction rate
 */
export function reactionRate(rateConstant, concentration, order) {
    const safeConc = Math.max(0, concentration);
    return rateConstant * Math.pow(safeConc, order);
}
/**
 * Calculate concentration change for first-order reaction.
 *
 * [A](t) = [A]₀ * e^(-kt)
 *
 * @param initialConc - Initial concentration [A]₀
 * @param rateConstant - Rate constant k
 * @param time - Time t
 * @returns Concentration at time t
 */
export function firstOrderKinetics(initialConc, rateConstant, time) {
    return initialConc * Math.exp(-rateConstant * time);
}
/**
 * Calculate concentration change for second-order reaction.
 *
 * 1/[A](t) = 1/[A]₀ + kt
 *
 * @param initialConc - Initial concentration [A]₀
 * @param rateConstant - Rate constant k
 * @param time - Time t
 * @returns Concentration at time t
 */
export function secondOrderKinetics(initialConc, rateConstant, time) {
    const invConc = 1 / initialConc + rateConstant * time;
    return invConc > 0 ? 1 / invConc : 0;
}
// ============================================================================
// TRANSPORT PHENOMENA
// ============================================================================
/**
 * Calculate flux using Fick's First Law of Diffusion.
 *
 * J = -D * dC/dx
 *
 * @param diffusionCoefficient - Diffusion coefficient D in m²/s
 * @param concentrationGradient - Concentration gradient dC/dx in (mol/m³)/m
 * @returns Flux J in mol/(m²·s)
 *
 * @example
 * ```ts
 * // Oxygen diffusing through water
 * const D = 1.8e-9; // m²/s
 * const gradient = -0.1; // mol/m⁴
 * const flux = ficksDiffusion(D, gradient);
 * ```
 */
export function ficksDiffusion(diffusionCoefficient, concentrationGradient) {
    return -diffusionCoefficient * concentrationGradient;
}
/**
 * Calculate concentration profile over time using 1D diffusion equation solution.
 *
 * C(x,t) = C₀ * erfc(x / (2 * sqrt(D*t)))
 *
 * This is for a semi-infinite medium with constant surface concentration.
 *
 * @param surfaceConc - Surface concentration C₀
 * @param distance - Distance from surface x
 * @param diffusionCoefficient - Diffusion coefficient D
 * @param time - Time t
 * @returns Concentration at position x and time t
 */
export function diffusionProfile(surfaceConc, distance, diffusionCoefficient, time) {
    if (time <= 0)
        return distance === 0 ? surfaceConc : 0;
    const argument = distance / (2 * Math.sqrt(diffusionCoefficient * time));
    return surfaceConc * erfc(argument);
}
/**
 * Complementary error function approximation.
 * erfc(x) = 1 - erf(x)
 */
function erfc(x) {
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);
    // Abramowitz and Stegun approximation
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    return sign < 0 ? 2 - y : y;
}
// ============================================================================
// FORCES
// ============================================================================
/**
 * Calculate Coulomb force between two point charges.
 *
 * F = k_e * |q₁ * q₂| / r²
 *
 * @param q1 - Charge 1 in Coulombs
 * @param q2 - Charge 2 in Coulombs
 * @param distance - Separation distance r in meters
 * @param coulombConstant - Coulomb constant k_e (default: 8.99×10⁹ N·m²/C²)
 * @returns Force magnitude in Newtons
 *
 * @example
 * ```ts
 * // Two protons separated by 1 nm
 * const e = ELEMENTARY_CHARGE;
 * const force = coulombForce(e, e, 1e-9); // ~2.3×10⁻¹⁰ N
 * ```
 */
export function coulombForce(q1, q2, distance, coulombConstant = COULOMB_CONSTANT) {
    if (distance <= 0)
        return Infinity;
    return coulombConstant * Math.abs(q1 * q2) / (distance * distance);
}
/**
 * Calculate gravitational force between two masses.
 *
 * F = G * m₁ * m₂ / r²
 *
 * @param m1 - Mass 1 in kg
 * @param m2 - Mass 2 in kg
 * @param distance - Separation distance r in meters
 * @param gravitationalConstant - Gravitational constant G (default: 6.674×10⁻¹¹)
 * @returns Force magnitude in Newtons
 *
 * @example
 * ```ts
 * // Earth and satellite
 * const earthMass = 5.97e24; // kg
 * const satMass = 1000; // kg
 * const altitude = 400000; // m (400 km)
 * const earthRadius = 6.371e6; // m
 * const force = newtonianGravity(earthMass, satMass, earthRadius + altitude);
 * ```
 */
export function newtonianGravity(m1, m2, distance, gravitationalConstant = GRAVITATIONAL_CONSTANT) {
    if (distance <= 0)
        return Infinity;
    return gravitationalConstant * m1 * m2 / (distance * distance);
}
/**
 * Calculate spring force using Hooke's Law.
 *
 * F = -k * x
 *
 * @param springConstant - Spring constant k in N/m
 * @param displacement - Displacement from equilibrium x in meters
 * @returns Restoring force in Newtons (negative sign indicates direction)
 *
 * @example
 * ```ts
 * const k = 100; // N/m
 * const x = 0.1; // m (10 cm stretched)
 * const force = hookeLaw(k, x); // -10 N (restoring)
 * ```
 */
export function hookeLaw(springConstant, displacement) {
    return -springConstant * displacement;
}
/**
 * Calculate spring potential energy.
 *
 * U = (1/2) * k * x²
 *
 * @param springConstant - Spring constant k in N/m
 * @param displacement - Displacement from equilibrium x in meters
 * @returns Potential energy in Joules
 */
export function springPotentialEnergy(springConstant, displacement) {
    return 0.5 * springConstant * displacement * displacement;
}
// ============================================================================
// FLUID DYNAMICS
// ============================================================================
/**
 * Calculate drag force using the drag equation.
 *
 * F_d = (1/2) * ρ * v² * C_d * A
 *
 * @param density - Fluid density ρ in kg/m³
 * @param velocity - Object velocity v in m/s
 * @param dragCoefficient - Drag coefficient C_d (dimensionless)
 * @param area - Cross-sectional area A in m²
 * @returns Drag force in Newtons (opposes motion)
 *
 * @example
 * ```ts
 * // Air drag on a sphere
 * const airDensity = 1.225; // kg/m³ at sea level
 * const velocity = 10; // m/s
 * const cd = 0.47; // sphere
 * const radius = 0.05; // m
 * const area = Math.PI * radius * radius;
 * const drag = dragForce(airDensity, velocity, cd, area);
 * ```
 */
export function dragForce(density, velocity, dragCoefficient, area) {
    const speed = Math.abs(velocity);
    return 0.5 * density * speed * speed * dragCoefficient * area * Math.sign(velocity);
}
/**
 * Calculate buoyant force using Archimedes' principle.
 *
 * F_b = ρ_fluid * V_displaced * g
 *
 * @param fluidDensity - Fluid density in kg/m³
 * @param displacedVolume - Volume of displaced fluid in m³
 * @param gravity - Gravitational acceleration (default: 9.80665 m/s²)
 * @returns Buoyant force in Newtons
 */
export function buoyantForce(fluidDensity, displacedVolume, gravity = STANDARD_GRAVITY) {
    return fluidDensity * displacedVolume * gravity;
}
// ============================================================================
// THERMODYNAMICS
// ============================================================================
/**
 * Calculate thermal energy change.
 *
 * Q = m * c * ΔT
 *
 * @param mass - Mass in kg
 * @param specificHeat - Specific heat capacity in J/(kg·K)
 * @param temperatureChange - Temperature change ΔT in K
 * @returns Heat energy in Joules
 */
export function thermalEnergy(mass, specificHeat, temperatureChange) {
    return mass * specificHeat * temperatureChange;
}
/**
 * Calculate root-mean-square speed of gas molecules.
 *
 * v_rms = sqrt(3 * R * T / M)
 *
 * @param temperature - Temperature in K
 * @param molarMass - Molar mass in kg/mol
 * @returns RMS speed in m/s
 */
export function rmsSpeed(temperature, molarMass) {
    return Math.sqrt(3 * IDEAL_GAS_CONSTANT * temperature / molarMass);
}
// ============================================================================
// MATHEMATICAL UTILITIES
// ============================================================================
/**
 * Linear interpolation between two values.
 *
 * @param a - Start value
 * @param b - End value
 * @param t - Interpolation parameter [0, 1]
 * @returns Interpolated value
 */
export function lerp(a, b, t) {
    return a + (b - a) * t;
}
/**
 * Clamp a value between min and max.
 *
 * @param value - Value to clamp
 * @param min - Minimum bound
 * @param max - Maximum bound
 * @returns Clamped value
 */
export function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
/**
 * Smooth step function (Hermite interpolation).
 *
 * Smoothstep(x) = 3x² - 2x³
 *
 * @param edge0 - Lower edge
 * @param edge1 - Upper edge
 * @param x - Value to smooth
 * @returns Smoothed value
 */
export function smoothstep(edge0, edge1, x) {
    const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
}
/**
 * Normalize an angle to the range [-π, π].
 *
 * @param angle - Angle in radians
 * @returns Normalized angle
 */
export function normalizeAngle(angle) {
    while (angle > Math.PI)
        angle -= 2 * Math.PI;
    while (angle < -Math.PI)
        angle += 2 * Math.PI;
    return angle;
}
/**
 * Sign function that returns -1, 0, or 1.
 *
 * @param x - Input value
 * @returns Sign of x
 */
export function sign(x) {
    return x > 0 ? 1 : x < 0 ? -1 : 0;
}
/**
 | * Safe division that returns 0 instead of NaN/Infinity.
 *
 * @param numerator - Numerator
 * @param denominator - Denominator
 * @returns numerator / denominator, or 0 if denominator is 0
 */
export function safeDivide(numerator, denominator) {
    return denominator !== 0 ? numerator / denominator : 0;
}
/**
 * Safe square root that returns 0 for negative inputs.
 *
 * @param x - Input value
 * @returns sqrt(x) or 0 if x < 0
 */
export function safeSqrt(x) {
    return x >= 0 ? Math.sqrt(x) : 0;
}
/**
 * Safe natural log that returns a small value for non-positive inputs.
 *
 * @param x - Input value
 * @param minLogValue - Minimum value to return for invalid inputs (default: -100)
 * @returns ln(x) or minLogValue if x <= 0
 */
export function safeLog(x, minLogValue = -100) {
    return x > 0 ? Math.log(x) : minLogValue;
}
/**
 * Smooth minimum function (useful for switching behavior).
 *
 * smin(a, b, k) = -ln(e^(-ka) + e^(-kb)) / k
 *
 * @param a - First value
 * @param b - Second value
 * @param k - Smoothing factor (higher = sharper transition)
 * @returns Smooth minimum
 */
export function smoothMin(a, b, k = 32) {
    return -Math.log(Math.exp(-k * a) + Math.exp(-k * b)) / k;
}
/**
 * Smooth maximum function.
 *
 * smax(a, b, k) = ln(e^(ka) + e^(kb)) / k
 *
 * @param a - First value
 * @param b - Second value
 * @param k - Smoothing factor (higher = sharper transition)
 * @returns Smooth maximum
 */
export function smoothMax(a, b, k = 32) {
    return Math.log(Math.exp(k * a) + Math.exp(k * b)) / k;
}
//# sourceMappingURL=primitives.js.map